import React from 'react';
import { Modal } from 'antd';
import './ConfirmModal.css';

export interface ConfirmModalConfig {
  visible: boolean;
  type: 'release' | 'pause' | 'delete' | 'advance' | 'publish' | 'resume' | 'custom';
  title?: string;
  content?: React.ReactNode;
  // 通用数据对象，支持不同页面的数据
  data?: {
    name?: string;        // 通用名称字段
    version?: string;      // 版本号
    title?: string;       // 标题
    id?: string;          // ID
    currentPhase?: number; // 当前阶段
    nextPhase?: number;   // 下一阶段
    [key: string]: any;   // 其他自定义字段
  };
  // 删除相关的配置
  deleteConfig?: {
    itemName?: string;    // 删除项的名称，如"版本"、"设备"、"用户"等
    warningText?: string; // 自定义警告文本
    showWarning?: boolean; // 是否显示警告框
  };
  // 推进阶段相关的配置
  advanceConfig?: {
    currentPercentage?: number; // 当前百分比
    nextPercentage?: number;    // 下一百分比
    hintText?: string;         // 自定义提示文本
  };
  onConfirm?: () => void;
  onCancel?: () => void;
  okText?: string;
  cancelText?: string;
  okButtonProps?: any;
  cancelButtonProps?: any;
  width?: number;
}

interface ConfirmModalProps {
  config: ConfirmModalConfig;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({ config }) => {
  const {
    visible,
    type,
    title,
    content,
    data,
    deleteConfig,
    advanceConfig,
    onConfirm,
    onCancel,
    okText,
    cancelText,
    okButtonProps,
    cancelButtonProps,
    width = 480,
  } = config;

  // 获取默认配置
  const getDefaultConfig = () => {
    switch (type) {
      case 'release':
        return {
          title: '确认发布版本?',
          content: (
            <div className="space-y-4">
              <p className="text-gray-700">
                您即将发布版本{data?.version || data?.name}。发布后，符合策略条件的设备将开始接收此更新。
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-blue-800 text-sm">
                  提示: 请确保已配置发布策略，包括时间控制和设备筛选条件。
                </p>
              </div>
            </div>
          ),
          okText: '确认发布',
          okButtonProps: { type: 'primary' as const },
        };
      case 'pause':
        return {
          title: '确认暂停发布?',
          content: (
            <div className="space-y-4">
              <p className="text-gray-700">
                您即将暂停版本{data?.version || data?.name}的发布。暂停后，设备将不再接收此版本的更新推送，但已下载的设备可以继续安装。
              </p>
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-red-800 text-sm">
                  提示: 您可以随时恢复发布，当前的发布配置将会保留。
                </p>
              </div>
            </div>
          ),
          okText: '确认暂停',
          okButtonProps: { type: 'primary' as const, danger: true },
        };
      case 'advance':
        const currentPercentage = advanceConfig?.currentPercentage || 30;
        const nextPercentage = advanceConfig?.nextPercentage || 50;
        const hintText = advanceConfig?.hintText || `当前阶段为${currentPercentage}%，推进后将为${nextPercentage}%。`;
        
        return {
          title: '确认推进阶段?',
          content: (
            <div className="space-y-4">
              <p className="text-gray-700">
                您即将推进版本{data?.version || data?.name}的灰度发布阶段。推进后，设备将接收更高比例的更新推送。
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-blue-800 text-sm">
                  提示: {hintText}
                </p>
              </div>
            </div>
          ),
          okText: '确认推进',
          okButtonProps: { type: 'primary' as const },
        };
      case 'resume':
        return {
          title: '确认恢复发布?',
          content: (
            <div className="space-y-4">
              <p className="text-gray-700">
                您即将恢复版本{data?.version || data?.name}的发布。恢复后，设备将开始接收此版本的更新推送。
              </p>
            </div>
          ),
          okText: '确认恢复',
          okButtonProps: { type: 'primary' as const },
        };
      case 'publish':
        return {
          title: '确认发布APK?',
          content: (
            <div className="space-y-4">
              <p className="text-gray-700">
                您即将发布APK版本{data?.version || data?.name}。发布后，符合条件的设备将收到更新推送。
              </p>
            </div>
          ),
          okText: '确认发布',
          okButtonProps: { type: 'primary' as const },
        };
      case 'delete':
        const itemName = deleteConfig?.itemName || '项目';
        const itemValue = data?.version || data?.name || data?.title || '此项';
        const warningText = deleteConfig?.warningText;
        const showWarning = deleteConfig?.showWarning !== false;
        
        return {
          title: `确认删除${itemName}?`,
          content: (
            <div className="space-y-4">
              <p className="text-gray-700">
                您即将删除{itemName}{itemValue}。此操作无法撤销，相关数据也将从服务器中永久删除。
              </p>
              {showWarning && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-red-800 text-sm">
                    <span className="font-bold">警告:</span> {warningText || `如果该${itemName}正在使用中，请先停止后再删除。`}
                  </p>
                </div>
              )}
            </div>
          ),
          okText: `确认删除`,
          okButtonProps: { type: 'primary' as const, danger: true },
        };
      case 'custom':
        return {
          title: title || '确认操作',
          content: content,
          okText: okText || '确认',
          okButtonProps: okButtonProps || { type: 'primary' as const },
        };
      default:
        return {
          title: '确认操作',
          content: <p>您确定要执行此操作吗？</p>,
          okText: '确认',
          okButtonProps: { type: 'primary' as const },
        };
    }
  };

  const defaultConfig = getDefaultConfig();

  // 处理确认按钮点击
  const handleOk = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  // 处理取消按钮点击
  const handleCancelClick = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Modal
      title={defaultConfig.title}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancelClick}
      okText={defaultConfig.okText}
      cancelText={cancelText || '取消'}
      okButtonProps={defaultConfig.okButtonProps}
      cancelButtonProps={cancelButtonProps || { type: 'default' }}
      width={width}
      centered
      className="confirm-modal"
      styles={{
        body: {
          background: 'var(--bg-secondary)',
          color: 'var(--text-primary)',
        },
        header: {
          background: 'var(--bg-tertiary)',
          borderBottom: '1px solid var(--border-primary)',
        },
        footer: {
          background: 'var(--bg-secondary)',
          borderTop: '1px solid var(--border-primary)',
        },
        mask: {
          backgroundColor: 'var(--bg-overlay)',
        },
      }}
    >
      {defaultConfig.content}
    </Modal>
  );
};

export default ConfirmModal;
